import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { httpOk, GET, POST } from 'helpers/api'

function toMinutes(hhmm) {
	if (!hhmm) return 0
	const [h, m] = String(hhmm).split(':').map(Number)
	return (h || 0) * 60 + (m || 0)
}

function Index() {
	const { t } = useTranslation()
	const [data, setData] = useState({ work_start: '08:00', work_end: '19:00', items: [] })

	async function load() {
		const res = await GET('/production-capacity', {}, { loader: true })
		if (httpOk(res)) setData(res.data)
	}

	useEffect(() => { load() }, []) // eslint-disable-line react-hooks/exhaustive-deps

	const workMin = Math.max(0, toMinutes(data.work_end) - toMinutes(data.work_start))
	const workH = Math.floor(workMin / 60)
	const workM = workMin % 60

	function setItem(i, field, val) {
		const items = [...data.items]
		items[i] = { ...items[i], [field]: val.replace(/[^\d.]/g, '') }
		setData({ ...data, items })
	}

	function setSetting(field, val) {
		setData({
			...data,
			settings: { ...(data.settings ?? {}), [field]: val.replace(/[^\d.]/g, '') },
		})
	}

	async function save() {
		const s = data.settings ?? {}
		const res = await POST('/production-capacity', {
			work_start: data.work_start,
			work_end: data.work_end,
			items: data.items.map(it => ({
				product_type: it.product_type,
				daily_volume: Number(it.daily_volume) || 0,
				// Bo'sh qoldirilsa NULL bo'ladi — o'sha turda sana tavsiyasi eski usulda ishlaydi
				run_volume: (it.run_volume === '' || it.run_volume === null || it.run_volume === undefined)
					? null : Number(it.run_volume) || null,
			})),
			setup_minutes: Number(s.setup_minutes) || 0,
			roll_open_minutes: Number(s.roll_open_minutes) || 0,
			workday_hours: Number(s.workday_hours) || 10,
		}, { loader: true })
		if (httpOk(res)) toast.success(t('save') + ' ✓')
	}

	/* Namuna hisob — korxona egasining misoli bilan tekshirish uchun:
	   «2000 metr bitta rulondan» va «2000 metr o'nta rulondan» necha soat chiqadi. */
	function exampleHours(rolls) {
		const prof = data.items.find(x => x.product_type === 4)
		const run = Number(prof?.run_volume) || 0
		const s = data.settings ?? {}
		const hours = Number(s.workday_hours) || 10
		if (run <= 0) return null

		const runHours = (2000 / run) * hours
		const setupHours = rolls * ((Number(s.setup_minutes) || 0) / 60)
		return (runHours + setupHours).toFixed(1)
	}

	function rate(vol) {
		const v = Number(vol) || 0
		if (workMin <= 0 || v <= 0) return 0
		return v / workMin
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">{t('production_capacity')}</h4>
				<button className="btn btn-primary btn-rounded" onClick={save}>
					<i className="uil-check me-1" />{t('save')}
				</button>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="alert alert-light border mb-4" style={{ fontSize: '0.85rem' }}>
						<i className="uil-info-circle me-1 text-primary" />
              {t('capacity_hint')}
					</div>

					{/* Ish vaqti */}
					<div className="mb-4">
						<label className="fw-bold mb-2 d-block">{t('work_hours')}</label>
						<div className="d-flex align-items-center gap-2 flex-wrap">
							<input type="time" className="form-control" style={{ width: 140 }}
								value={data.work_start ?? '08:00'}
								onChange={(e) => setData({ ...data, work_start: e.target.value })} />
							<span className="text-muted">—</span>
							<input type="time" className="form-control" style={{ width: 140 }}
								value={data.work_end ?? '19:00'}
								onChange={(e) => setData({ ...data, work_end: e.target.value })} />
							<span className="badge bg-light text-dark border ms-2" style={{ fontSize: '0.85rem' }}>
								{workH} {t('hour')}{workM > 0 ? ` ${workM} ${t('minute')}` : ''} = {workMin} {t('minute')}/{t('day')}
							</span>
						</div>
					</div>

					{/* Bo'limlar */}
					<div className="table-responsive">
						<table className="table table-bordered align-middle mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: 33 }} className="text-center">#</th>
									<th>{t('department')}</th>
									<th style={{ width: '24%' }}>{t('daily_volume')}</th>
									{/* SOF TEZLIK — sana tavsiyasi uchun (rulon almashtirmasdan) */}
									<th style={{ width: '24%' }}>
										{t('run_volume')}
										<i className="uil-info-circle ms-1 text-primary" title={t('run_volume_hint')} />
									</th>
									<th style={{ width: '18%' }} className="text-end">{t('speed_auto')}</th>
								</tr>
							</thead>
							<tbody>
								{data.items.map((it, i) => (
									<tr key={it.product_type}>
										<td className="text-center">{i + 1}</td>
										<td className="fw-semibold">
											{it.name}
											{it.product_type === 4 &&
												<span className="badge bg-light text-primary border ms-2" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
													<i className="uil-tachometer-fast-alt me-1" />{t('plan_only')}
												</span>}
										</td>
										<td>
											<div className="input-group">
												<input type="text" inputMode="decimal" className="form-control"
													placeholder="1500"
													value={it.daily_volume ?? ''}
													onChange={(e) => setItem(i, 'daily_volume', e.target.value)} />
												<span className="input-group-text">{it.unit}/{t('day')}</span>
											</div>
										</td>
										<td>
											<div className="input-group">
												<input type="text" inputMode="decimal" className="form-control"
													placeholder={it.product_type === 4 ? '8000' : '—'}
													value={it.run_volume ?? ''}
													onChange={(e) => setItem(i, 'run_volume', e.target.value)} />
												<span className="input-group-text">{it.unit}/{t('day')}</span>
											</div>
										</td>
										<td className="text-end">
											{it.product_type === 4
												? <span className="text-muted small">{t('speed_separate')}</span>
												: rate(it.daily_volume) > 0
													? <span className="fw-bold text-primary">{rate(it.daily_volume).toFixed(2)} {it.unit}/min</span>
													: <span className="text-muted">—</span>}
										</td>
									</tr>
								))}
								{data.items.length === 0 &&
									<tr><td colSpan={5} className="text-center text-muted py-3">{t('loading')}</td></tr>
								}
							</tbody>
						</table>
					</div>

					{/* ═══ RULON SOZLASH VAQTI ═══
					    Sana tavsiyasi uchun: asosiy vaqt metrda emas, rulon almashtirishda
					    ketadi. «2000 m bitta rulondan 2-3 soat, 10 ta rulondan 1 kun»
					    (korxona egasi, 2026-08-17). */}
					<div className="mt-4">
						<label className="fw-bold mb-2 d-block">{t('roll_setup_title')}</label>
						<div className="alert alert-light border" style={{ fontSize: '0.85rem' }}>
							<i className="uil-info-circle me-1 text-primary" />
							{t('roll_setup_hint')}
						</div>

						<div className="row">
							<div className="form-group col-md-4">
								<label>{t('setup_minutes')}</label>
								<div className="input-group">
									<input type="text" inputMode="numeric" className="form-control" placeholder="45"
										value={data.settings?.setup_minutes ?? ''}
										onChange={(e) => setSetting('setup_minutes', e.target.value)} />
									<span className="input-group-text">{t('minute')}</span>
								</div>
							</div>
							<div className="form-group col-md-4">
								<label>{t('roll_open_minutes')}</label>
								<div className="input-group">
									<input type="text" inputMode="numeric" className="form-control" placeholder="15"
										value={data.settings?.roll_open_minutes ?? ''}
										onChange={(e) => setSetting('roll_open_minutes', e.target.value)} />
									<span className="input-group-text">{t('minute')}</span>
								</div>
							</div>
							<div className="form-group col-md-4">
								<label>{t('workday_hours')}</label>
								<div className="input-group">
									<input type="text" inputMode="decimal" className="form-control" placeholder="10"
										value={data.settings?.workday_hours ?? ''}
										onChange={(e) => setSetting('workday_hours', e.target.value)} />
									<span className="input-group-text">{t('hour')}</span>
								</div>
							</div>
						</div>

						{/* Tekshiruv: raqamlar to'g'ri qo'yilganini shu ikki misolda darhol ko'rasiz */}
						{exampleHours(1) &&
							<div className="mt-2" style={{ fontSize: '0.88rem' }}>
								<span className="text-muted">{t('example')}: </span>
								2000 m — 1 {t('roll')}: <b>{exampleHours(1)} {t('hour')}</b>
								{' · '}
								2000 m — 10 {t('roll')}: <b>{exampleHours(10)} {t('hour')}</b>
							</div>
						}
					</div>

					<div className="d-flex justify-content-end mt-3">
						<button className="btn btn-primary" onClick={save}>
							<i className="uil-check me-1" />{t('save')}
						</button>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index
